import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Eye, EyeOff, Loader2, Building2, Gamepad2, ArrowLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AnimatedLogo from "../components/AnimatedLogo";
import api from "../lib/api";

const loginSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(1, "Mot de passe requis"),
});

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(null); // null = choice, 'organizer' ou 'participant'

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(loginSchema),
  });

  // Connexion organisateur
  const onSubmitOrganizer = async (data) => {
    setLoading(true);
    try {
      await login(data.email, data.password);
      toast.success("Connexion réussie");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Erreur de connexion";
      if (error.code === "ERR_NETWORK") {
        toast.error("Erreur réseau - Vérifiez votre connexion internet");
      } else if (error.response?.status === 401) {
        toast.error("Email ou mot de passe incorrect");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Connexion participant
  const onSubmitParticipant = async (data) => {
    setLoading(true);
    try {
      const response = await api.post("/member-portal/login", {
        email: data.email,
        password: data.password
      });
      
      localStorage.setItem("tireo_member_token", response.data.token);
      localStorage.setItem("tireo_member", JSON.stringify(response.data.member));
      
      toast.success("Connexion réussie");
      navigate("/play");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Email ou mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  // Écran de choix initial
  if (!userType) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-lg">
          <div className="flex justify-center mb-6 sm:mb-8">
            <AnimatedLogo linkTo="/" showAnimation={true} size="default" />
          </div>

          <Card className="border-border shadow-lg animate-fade-in-up">
            <CardHeader className="text-center pb-4 px-4 sm:px-6">
              <CardTitle className="text-xl sm:text-2xl" style={{ fontFamily: 'Outfit' }}>Connexion</CardTitle>
              <CardDescription>Choisissez votre type de compte</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Bouton Organisateur */}
              <button
                onClick={() => setUserType('organizer')}
                className="w-full p-6 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all group text-left"
                data-testid="login-choice-organizer"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
                      Organisateur
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Entreprise ou association qui organise des tirages au sort et sondages
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </button>

              {/* Bouton Participant */}
              <button
                onClick={() => setUserType('participant')}
                className="w-full p-6 rounded-xl border-2 border-border hover:border-amber-500 hover:bg-amber-500/5 transition-all group text-left"
                data-testid="login-choice-participant"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                    <Gamepad2 className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-foreground group-hover:text-amber-600 transition-colors">
                      Participant
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Joueur qui participe aux tirages au sort et jeux
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-amber-500 transition-colors" />
                </div>
              </button>

              <div className="pt-4 text-center">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
                  ← Retour à l'accueil
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Formulaire de connexion (organisateur ou participant)
  const isParticipant = userType === 'participant';
  const gradientFrom = isParticipant ? "from-amber-500" : "from-blue-500";
  const gradientTo = isParticipant ? "to-orange-600" : "to-indigo-600";

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6 sm:mb-8">
          <AnimatedLogo linkTo="/" showAnimation={true} size="default" />
        </div>

        <Card className="border-border shadow-lg animate-fade-in-up">
          <CardHeader className="text-center pb-2 px-4 sm:px-6">
            <button 
              onClick={() => setUserType(null)}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Changer de type de compte
            </button>
            
            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${gradientFrom} ${gradientTo} flex items-center justify-center mx-auto mb-4`}>
              {isParticipant ? (
                <Gamepad2 className="w-8 h-8 text-white" />
              ) : (
                <Building2 className="w-8 h-8 text-white" />
              )}
            </div>
            
            <CardTitle className="text-xl sm:text-2xl" style={{ fontFamily: 'Outfit' }}>
              {isParticipant ? "Espace Joueur" : "Espace Organisateur"}
            </CardTitle>
            <CardDescription>
              {isParticipant 
                ? "Connectez-vous pour participer aux jeux" 
                : "Accédez à votre espace de gestion"
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit(isParticipant ? onSubmitParticipant : onSubmitOrganizer)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  className="h-11 bg-slate-50 dark:bg-slate-800 border-transparent focus:border-primary text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  data-testid="login-email-input"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="h-11 bg-slate-50 dark:bg-slate-800 border-transparent focus:border-primary text-foreground placeholder:text-slate-400 dark:placeholder:text-slate-500 pr-10"
                    data-testid="login-password-input"
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <Button 
                type="submit" 
                className={`w-full h-11 rounded-full ${isParticipant ? 'bg-amber-500 hover:bg-amber-600' : 'bg-primary hover:bg-primary/90'}`}
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  "Se connecter"
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Pas encore de compte ?{" "}
              {isParticipant ? (
                <Link to="/join" className="text-amber-600 hover:underline font-medium">
                  Créer un compte joueur
                </Link>
              ) : (
                <Link to="/register" className="text-primary hover:underline font-medium">
                  Créer un compte organisateur
                </Link>
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
